import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./design/Navbar.css";
import { getApiUrl, getImageUrl } from "../utils/api";

// --- Data Constants ---
const budgetOptions = [
  "Below ₹10 Lakh",
  "₹10 Lakh - ₹25 Lakh",
  "₹25 Lakh - ₹50 Lakh",
  "Above ₹50 Lakh",
];

const modelTypes = [
  { name: "FOFO", description: "You invest, own, and operate — brand guides, you run the show." },
  { name: "FOCO", description: "You fund it, but the company operates — lower effort, shared returns." },
  { name: "COCO", description: "Company own & company operate" },
];

// --- SVG Icon Components ---
const SearchIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>);
const LocationIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>);
const CategoryIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>);
const BudgetIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>);
const ModelIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>);
const TypeIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>);
const CloseIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>);

// --- UI Components ---
const Spinner = () => <div className="spinner"></div>;

const FilterModal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <h3 className="filter-modal-title">{title}</h3>
          <button onClick={onClose} className="filter-modal-close-button">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const SearchBar = () => {
  const navigate = useNavigate();
  const searchWrapperRef = useRef(null);
  const lastScrollY = useRef(0);
  const programmaticScrollRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // --- State Management ---
  const [activeModal, setActiveModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedState, setSelectedState] = useState({ id: null, name: "" });
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState({ states: false, cities: false, categories: false });
  const [errors, setErrors] = useState({ states: null, cities: null, categories: null });
  const [isFilterSectionVisible, setIsFilterSectionVisible] = useState(true);

  // --- Data Fetching ---
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(prev => ({ ...prev, categories: true, states: true }));
      try {
        const [catRes, stateRes] = await Promise.all([
          fetch(getApiUrl("get-master-category.php")),
          fetch(getApiUrl("get-indian-states.php"))
        ]);
        if (!catRes.ok) throw new Error(`Category API error! Status: ${catRes.status}`);
        if (!stateRes.ok) throw new Error(`State API error! Status: ${stateRes.status}`);
        
        const catData = await catRes.json();
        const stateData = await stateRes.json();

        setCategories(Array.isArray(catData) ? catData.map(item => ({ id: item.mas_cat_id, name: item.mas_cat_name })) : []);
        setStates(Array.isArray(stateData) ? stateData : []);
      } catch (err) {
        console.error("Initial data fetch error:", err);
        setErrors(prev => ({ ...prev, categories: "Failed to load data.", states: "Failed to load data." }));
      } finally {
        setIsLoading(prev => ({ ...prev, categories: false, states: false }));
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!selectedState.id) {
      setCities([]);
      return;
    }
    const fetchCities = async () => {
      setIsLoading(prev => ({ ...prev, cities: true }));
      setErrors(prev => ({ ...prev, cities: null }));
      try {
        const res = await fetch(getApiUrl(`get-cities.php?state_id=${selectedState.id}`));
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Cities API error:", err);
        setErrors(prev => ({ ...prev, cities: "Failed to load cities." }));
      } finally {
        setIsLoading(prev => ({ ...prev, cities: false }));
      }
    };
    fetchCities();
  }, [selectedState.id]);

  // --- Brand Search Function ---
  const searchBrands = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(getApiUrl(`search-brands.php?q=${encodeURIComponent(query)}`));
      const data = await response.json();
      
      if (data.success && Array.isArray(data.brands)) {
        setSuggestions(data.brands);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error("Brand search error:", err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchBrands(searchTerm);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchBrands]);

  // --- Scroll Handling ---
  const handleScroll = useCallback(() => {
    if (programmaticScrollRef.current) {
        return;
    }

    const currentScrollY = window.scrollY;

    if (currentScrollY > lastScrollY.current && isFilterSectionVisible) {
      if (currentScrollY > 10) { 
        setIsFilterSectionVisible(false);
      }
    }

    lastScrollY.current = currentScrollY;
  }, [isFilterSectionVisible]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);
  
  // --- Click Outside Handler ---
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // --- Handlers ---
  const handleShowFilters = () => {
    setIsFilterSectionVisible(true);
    programmaticScrollRef.current = true;
    searchWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 800);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleBrandSuggestionClick = (brandId) => {
    setSearchTerm("");
    setShowSuggestions(false);
    navigate(`/brand/${brandId}`);
  };

  // Category toggle for modal - navigates to category page
  const handleCategoryToggle = (categoryId, categoryName) => {
    navigate(`/category?mas_cat=${categoryId}`);
    setActiveModal(null);
  };

  // Model type selection - redirects to /modal?id=1, /modal?id=2, etc.
  const handleModelSelect = (modelName, modelId) => {
    setActiveModal(null);
    navigate(`/modal?id=${modelId}`);
  };

  // Budget selection - redirects to /budget?budget=value
  const handleBudgetSelect = (budgetOption) => {
    setSelectedBudget(budgetOption);
    setActiveModal(null);
    navigate(`/budget?budget=${encodeURIComponent(budgetOption)}`);
  };
  
  const handleStateSelect = (state) => {
    setSelectedState({ id: state.id, name: state.name });
    setSelectedCity("");
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.append("q", searchTerm);
    if (selectedState.name) params.append("state", selectedState.name);
    if (selectedCity) params.append("city", selectedCity);
    if (selectedCategories.length) params.append("categories", selectedCategories.join(","));
    if (selectedBudget) params.append("budget", selectedBudget);
    if (selectedModel) params.append("model", selectedModel);
    if (categoryType) params.append("categoryType", categoryType);

    if (params.toString()) {
      navigate(`/search-results?${params.toString()}`);
    } else {
      navigate("/franchises");
    }
    setShowSuggestions(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedState({ id: null, name: "" });
    setSelectedCity("");
    setSelectedCategories([]);
    setSelectedBudget("");
    setSelectedModel("");
    setCategoryType("");
    setSearchTerm("");
    setActiveModal(null);
  };

  const locationDisplay = selectedCity ? `${selectedCity}, ${selectedState.name}` : selectedState.name || "Select Location";
  const filteredCategories = categories.filter(cat => cat.name.toLowerCase().includes(categorySearchTerm.toLowerCase()));

  return (
    <section id="search-bar" className="search-filters-wrapper" ref={searchWrapperRef}>
      <div className="search-main-area">
        <div className="search-input-wrapper">
          <span className="search-icon"><SearchIcon /></span>
          <input 
            type="text" 
            placeholder="Search by brand, keyword, or industry..." 
            className="search-input" 
            value={searchTerm} 
            onChange={handleSearchInputChange}
            autoComplete="off"
          />
          
          {/* Brand Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions brand-suggestions">
              {isSearching && <div className="suggestion-loading">Searching...</div>}
              {!isSearching && suggestions.map((brand) => (
                <div 
                  key={brand.id || brand.register_id} 
                  className="suggestion-item brand-suggestion-item"
                  onClick={() => handleBrandSuggestionClick(brand.id || brand.register_id)}
                >
                  <div className="suggestion-brand-logo">
                    {brand.logo ? (
                      <img src={getImageUrl(brand.logo)} alt={brand.name} onError={(e) => { e.target.src = '/images/placeholder.png'; }} />
                    ) : (
                      <div className="suggestion-brand-placeholder">🏪</div>
                    )}
                  </div>
                  <div className="suggestion-brand-info">
                    <div className="suggestion-brand-name">{brand.name}</div>
                    <div className="suggestion-brand-details">
                      <span className="suggestion-brand-category">{brand.sector || brand.category_name || "Franchise"}</span>
                      {brand.min_investment && (
                        <span className="suggestion-brand-investment">
                          ₹{formatIndianCurrency(brand.min_investment)} - ₹{formatIndianCurrency(brand.max_investment)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* No Results Message */}
          {showSuggestions && suggestions.length === 0 && searchTerm.trim().length >= 2 && !isSearching && (
            <div className="search-suggestions">
              <div className="suggestion-no-results">No brands found. Try different keywords.</div>
            </div>
          )}
        </div>
        
        <button onClick={handleSearch} className="search-button">
            <SearchIcon /> <span>Search</span>
        </button>

        {!isFilterSectionVisible && (
            <button className="show-filters-btn" onClick={handleShowFilters}>Show Filters</button>
        )}
      </div>

      {isFilterSectionVisible && (
        <div className="filters-container">
            <div className="filters-grid">
                <button className="filter-control" onClick={() => setActiveModal("location")}>
                  <div className="filter-control-header"><LocationIcon/> Location</div>
                  <p className="filter-control-value">{locationDisplay}</p>
                </button>
                <button className="filter-control" onClick={() => setActiveModal("categories")}>
                  <div className="filter-control-header"><CategoryIcon/> Categories</div>
                  <p className="filter-control-value">{selectedCategories.length ? `${selectedCategories.length} selected` : "All Categories"}</p>
                </button>
                <button className="filter-control" onClick={() => setActiveModal("budget")}>
                  <div className="filter-control-header"><BudgetIcon/> Budget</div>
                  <p className="filter-control-value">{selectedBudget || "Any Budget"}</p>
                </button>
                <button className="filter-control" onClick={() => setActiveModal("model")}>
                  <div className="filter-control-header"><ModelIcon/> Model Type</div>
                  <p className="filter-control-value">{selectedModel || "Any Model"}</p>
                </button>
                <button className="filter-control" onClick={() => setActiveModal("categoryType")}>
                  <div className="filter-control-header"><TypeIcon/> Category Type</div>
                  <p className="filter-control-value">{categoryType || "Any Type"}</p>
                </button>
            </div>
            <div className="filters-actions">
              <button onClick={clearAllFilters} className="clear-filters-btn">
                Clear All Filters
              </button>
            </div>
        </div>
      )}

      {/* --- Modals --- */}
      <FilterModal isOpen={activeModal === "location"} onClose={() => setActiveModal(null)} title="Select Location">
        <div className="filter-modal-body">
          <div className="location-selection-grid">
            <div className="location-column">
              <h4 className="column-title">State</h4>
              <div className="location-column-list">
                {isLoading.states ? <Spinner/> : errors.states ? <p className="filter-modal-error">{errors.states}</p> : 
                  states.map(state => (
                    <button 
                      key={state.id} 
                      onClick={() => handleStateSelect(state)} 
                      className={`filter-modal-list-button ${selectedState.id === state.id ? "selected" : ""}`}
                    >
                      {state.name}
                    </button>
                  ))
                }
              </div>
            </div>
            <div className="location-column">
              <h4 className="column-title">City</h4>
              <div className="location-column-list">
                {!selectedState.id ? <p className="filter-modal-prompt">Select a state first</p> :
                  isLoading.cities ? <Spinner/> : errors.cities ? <p className="filter-modal-error">{errors.cities}</p> : 
                  cities.map(city => (
                    <button 
                      key={city.id} 
                      onClick={() => setSelectedCity(city.name)} 
                      className={`filter-modal-list-button ${selectedCity === city.name ? "selected" : ""}`}
                    >
                      {city.name}
                    </button>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
        <div className="filter-modal-footer">
          <button onClick={() => {setSelectedState({id: null, name: ""}); setSelectedCity("");}} className="filter-modal-clear-button">Clear</button>
          <button onClick={() => setActiveModal(null)} className="filter-modal-apply-button">Apply</button>
        </div>
      </FilterModal>
      
      {/* Categories Modal */}
      <FilterModal isOpen={activeModal === "categories"} onClose={() => setActiveModal(null)} title={`Categories (${selectedCategories.length})`}>
        <div className="filter-modal-body">
          <div className="filter-modal-search-wrapper">
            <span className="filter-modal-search-icon"><SearchIcon /></span>
            <input 
              type="text" 
              placeholder="Search categories..." 
              className="filter-modal-search-input" 
              value={categorySearchTerm} 
              onChange={(e) => setCategorySearchTerm(e.target.value)} 
            />
          </div>
          <div className="filter-modal-scroll-list">
            {isLoading.categories ? <Spinner/> : errors.categories ? <p className="filter-modal-error">{errors.categories}</p> : 
              filteredCategories.length > 0 ? filteredCategories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="filter-modal-checkbox-label"
                  onClick={() => handleCategoryToggle(cat.id, cat.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>{cat.name}</span>
                </div>
              )) : <p className="filter-modal-no-results">No categories found.</p>
            }
          </div>
        </div>
        <div className="filter-modal-footer">
          <button onClick={() => setSelectedCategories([])} className="filter-modal-clear-button">Clear All</button>
          <button onClick={() => setActiveModal(null)} className="filter-modal-apply-button">Apply</button>
        </div>
      </FilterModal>

      {/* Budget Modal - Updated to navigate to /budget */}
      <FilterModal isOpen={activeModal === "budget"} onClose={() => setActiveModal(null)} title="Select Budget">
        <div className="filter-modal-body">
          <div className="filter-modal-button-list">
            {budgetOptions.map((option) => (
              <button 
                key={option} 
                onClick={() => handleBudgetSelect(option)} 
                className={`filter-modal-list-button ${selectedBudget === option ? "selected" : ""}`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-modal-footer">
          <button onClick={() => { setSelectedBudget(""); setActiveModal(null); }} className="filter-modal-clear-button">Clear</button>
        </div>
      </FilterModal>

      {/* Model Type Modal */}
      <FilterModal isOpen={activeModal === "model"} onClose={() => setActiveModal(null)} title="Select Model Type">
        <div className="filter-modal-body">
          <div className="filter-modal-button-list">
            {modelTypes.map((type, index) => (
              <button 
                key={type.name} 
                onClick={() => handleModelSelect(type.name, index + 1)} 
                className={`filter-modal-list-button-detailed ${selectedModel === type.name ? "selected" : ""}`}
              >
                <p className="title">{type.name}</p>
                <p className="description">{type.description}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="filter-modal-footer">
          <button onClick={() => { setSelectedModel(""); setActiveModal(null); }} className="filter-modal-clear-button">Clear</button>
        </div>
      </FilterModal>

      {/* Category Type Modal */}
      <FilterModal isOpen={activeModal === "categoryType"} onClose={() => setActiveModal(null)} title="Select Category Type">
        <div className="filter-modal-body">
          <div className="filter-modal-button-group">
            <button 
              onClick={() => {setCategoryType("Master"); setActiveModal(null);}} 
              className={`filter-modal-group-button ${categoryType === "Master" ? "selected" : ""}`}
            >
              Master
            </button>
            <button 
              onClick={() => { setCategoryType("Single"); setActiveModal(null);}} 
              className={`filter-modal-group-button ${categoryType === "Single" ? "selected" : ""}`}
            >
              Unit
            </button>
          </div>
        </div>
        <div className="filter-modal-footer">
          <button onClick={() => { setCategoryType(""); setActiveModal(null); }} className="filter-modal-clear-button">Clear</button>
        </div>
      </FilterModal>
    </section>
  );
};

// Helper function for currency formatting
function formatIndianCurrency(number) {
  if (!number || isNaN(number)) return "—";
  if (number >= 10000000) {
    const crores = number / 10000000;
    return `${parseFloat(crores.toFixed(1)).toString().replace(/\.0$/, '')} cr`;
  } else {
    const lacs = number / 100000;
    return `${parseFloat(lacs.toFixed(0))} lacs`;
  }
}

export default SearchBar;